import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { format, getTimezoneOffset } from "date-fns-tz";
import { ChevronDown, Star } from "lucide-react";
import { ScrollArea } from "../../components/ui/scroll-area";
import { timeZones } from "../../data/time-zones";
import { Resource } from "../../types/resource";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { browserTimezone, formattedDuration } from "@/helper/utility";
import { EventDuration } from "../../types/event-duration";

const today = new Date();

const getTimezoneOffsetInfo = (tz: string) => {
  const offsetStr = format(today, "xxx", { timeZone: tz });
  const offsetMs = getTimezoneOffset(tz, today);
  const offsetMin = offsetMs / (1000 * 60);

  return {
    offset: `GMT${offsetStr}`,
    minutes: offsetMin,
  };
};

export function TimezoneAndDuration({
  resource,
  durations,
  duration,
  onTimezoneChange,
  onDurationChange,
}: {
  resource: Resource;
  durations: EventDuration[];
  duration: number;
  onTimezoneChange: (tz: string) => void;
  onDurationChange: (d: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(browserTimezone);
  const [search, setSearch] = useState("");

  const handleTimezoneChange = (tz: string) => {
    setSelectedTimezone(tz);
    onTimezoneChange(tz);
  };

  const filteredTimezones = useMemo(() => {
    return timeZones
      .filter((tz) => tz.toLowerCase().includes(search.toLowerCase()))
      .map((tz) => ({
        name: tz,
        ...getTimezoneOffsetInfo(tz),
      }))
      .sort((a, b) => a.minutes - b.minutes);
  }, [search]);

  return (
    <div className="flex justify-end items-center gap-2">
      {!resource.settings?.hide_timezone && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 gap-2">
              <span>
                {selectedTimezone
                  ? getTimezoneOffsetInfo(selectedTimezone).offset
                  : ""}
              </span>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <div className="sticky top-0 bg-background z-10 p-2 border-b">
              <Input
                type="search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-7 text-sm"
              />
            </div>
            <ScrollArea className="h-64">
              <div className="p-1">
                {filteredTimezones.map((tz) => (
                  <div
                    key={tz.name}
                    onClick={() => {
                      handleTimezoneChange(tz.name);
                      setOpen(false);
                    }}
                    className="p-2 hover:bg-muted cursor-pointer flex justify-between items-center text-sm rounded-md m-1"
                  >
                    <span>{tz.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {tz.offset}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-2">
            {duration}m
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px]">
          <DropdownMenuLabel>Select a Duration</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {durations.map((d) => (
            <DropdownMenuItem
              key={d.minutes}
              onClick={() => onDurationChange(d.minutes)}
              className="gap-2 flex justify-between"
            >
              <span> {formattedDuration(d.minutes)}</span>
              {d.is_default && (
                <Star
                  className="h-4 w-4 text-yellow-500 dark:text-yellow-400"
                  fill="currentColor"
                />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
